import { useTranslation } from 'next-i18next';
import { useState, useEffect } from 'react';
import { FooterButtons } from '@components/templates/job/after-apply/FooterButtons';
import { bjToast, TailwindSpinner } from '@components/common';
import { useSwrJobRequirements } from '@hooks/useSwrApi/endpoints/job-requirements/useSwrJobRequirements';
import { useSwrSaveRequirementAnswers } from '@hooks/useSwrApi/endpoints/job-requirements/useSwrSaveRequirementAnswers';

interface RequirementQuestionsProps {
  jobSlug: string;
  locale: Locale;
  onSaveSuccess: () => void;
}

export const RequirementQuestions = ({ jobSlug, locale, onSaveSuccess }: RequirementQuestionsProps) => {
  const { t } = useTranslation('common');

  // Fetch requirement questions
  const {
    data: requirementData,
    isLoading: isFetchingQuestions,
    error: fetchError,
  } = useSwrJobRequirements({
    locale,
    jobSlug,
    shouldFetch: !!jobSlug,
  });

  // State for answers: Map<questionId, answerValue | undefined>
  // undefined means not answered yet
  const [answers, setAnswers] = useState<Map<number, boolean | undefined>>(new Map());

  // Initialize answers map when questions load (but leave values undefined)
  useEffect(() => {
    if (requirementData?.questions) {
      const initialAnswers = new Map<number, boolean | undefined>();
      requirementData.questions.forEach((question) => {
        if (question.questionId) {
          // No default value - leave undefined to force user selection
          initialAnswers.set(question.questionId, undefined);
        }
      });
      setAnswers(initialAnswers);
    }
  }, [requirementData]);

  // Save mutation
  const {
    trigger: saveAnswers,
    isMutating: isSaving,
  } = useSwrSaveRequirementAnswers({
    locale,
    successCallback: () => {
      bjToast.success(t('apply.wizard.requirements.success'));
      onSaveSuccess();
    },
    errorCallback: () => {
      bjToast.error(t('apply.wizard.requirements.error'));
    },
  });

  const handleAnswerChange = (questionId: number, value: boolean) => {
    setAnswers((prev) => {
      const newAnswers = new Map(prev);
      newAnswers.set(questionId, value);
      return newAnswers;
    });
  };

  const onSubmit = () => {
    // Validate: ensure all questions are answered
    const totalQuestions = requirementData?.questions?.length || 0;
    const answeredQuestions = Array.from(answers.values()).filter((answer) => answer !== undefined).length;

    if (answeredQuestions < totalQuestions) {
      bjToast.error(t('apply.wizard.requirements.validation.all.required'));
      return;
    }

    // Build payload - filter out undefined values
    const textAnswers = Array.from(answers.entries())
      .filter(([, answerValue]) => answerValue !== undefined)
      .map(([questionId, answerValue]) => ({
        questionId,
        answerValue: answerValue!,
      }));

    void saveAnswers({
      textAnswers,
    });
  };

  // Auto-proceed if no questions (shouldn't happen, but handle gracefully)
  useEffect(() => {
    if (requirementData?.questions?.length === 0) {
      onSaveSuccess();
    }
  }, [requirementData?.questions?.length, onSaveSuccess]);

  // Loading state
  if (isFetchingQuestions) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <TailwindSpinner />
      </div>
    );
  }

  // Error state
  if (fetchError || !requirementData?.questions) {
    return (
      <div className="text-center">
        <p className="text-error">{t('apply.wizard.requirements.fetch.error')}</p>
      </div>
    );
  }

  // No questions (shouldn't happen, but handle gracefully)
  if (requirementData.questions.length === 0) {
    return null;
  }

  return (
    <div>
      <p className="pb-6 text-lg">
        {t('apply.wizard.requirements.subtitle')}
      </p>

      <div className="space-y-4">
        {requirementData.questions.map((question) => {
          if (!question.questionId) return null;

          const currentAnswer = answers.get(question.questionId);

          return (
            <div key={question.questionId} className="rounded-lg border border-input p-4">
              <p className="mb-3 font-medium text-ink-dark">{question.question}</p>

              <div className="flex gap-4">
                {/* Yes option */}
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name={`question_${question.questionId}`}
                    checked={currentAnswer === true}
                    onChange={() => handleAnswerChange(question.questionId!, true)}
                    className="size-4 cursor-pointer accent-primary"
                  />
                  <span className="text-sm text-ink-medium">
                    {t('apply.wizard.requirements.yes')}
                  </span>
                </label>

                {/* No option */}
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name={`question_${question.questionId}`}
                    checked={currentAnswer === false}
                    onChange={() => handleAnswerChange(question.questionId!, false)}
                    className="size-4 cursor-pointer accent-primary"
                  />
                  <span className="text-sm text-ink-medium">
                    {t('apply.wizard.requirements.no')}
                  </span>
                </label>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6">
        <FooterButtons
          submitType="button"
          onSubmit={onSubmit}
          isLoading={isSaving}
        />
      </div>
    </div>
  );
};
